import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

const FULL_DASH_ARRAY = 1015;
const INITIAL_MINUTES = 30;
const DELAY_MINUTES = 10;

export default function CountdownTimer({ order, track }) {
  const timerIntervalRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [phase, setPhase] = useState("initial"); // "initial" | "delay"

  // Check if order has been assigned to a courier
  const hasCourier = track?.courier_orders?.length > 0 && track?.courier_orders?.[0]?.courier_id;
  
  // Check if order is completed/delivered
  const orderStatus =
    track?.courier_orders?.length > 0
      ? track?.courier_orders?.[0]?.status
      : order?.status;
  const isCompleted =
    orderStatus === "delivered" || orderStatus === "completed";

  // Initialise timer based on courier assignment time
  useEffect(() => {
    if (isCompleted || !hasCourier) return;

    // Use the courier assignment time instead of order creation time
    const courierAssignedAt = track?.courier_orders?.[0]?.created_at;
    if (!courierAssignedAt) return;

    const createdAt = moment(courierAssignedAt, moment.ISO_8601);
    const now = moment();
    const elapsedSeconds = now.diff(createdAt, "seconds");
    const initialDuration = INITIAL_MINUTES * 60;

    if (elapsedSeconds < initialDuration) {
      // Still within the first 30 minutes
      const remaining = initialDuration - elapsedSeconds;
      setTotalDuration(initialDuration);
      setTimeLeft(remaining);
      setPhase("initial");
    } else {
      // Past 30 mins — figure out which delay window we're in
      const overtime = elapsedSeconds - initialDuration;
      const delayDuration = DELAY_MINUTES * 60;
      const positionInCurrentWindow = overtime % delayDuration;
      const remaining = delayDuration - positionInCurrentWindow;
      setTotalDuration(delayDuration);
      setTimeLeft(remaining);
      setPhase("delay");
    }
  }, [track?.courier_orders, hasCourier, isCompleted]);

  // Tick the timer down
  useEffect(() => {
    if (isCompleted || timeLeft === 0 || !hasCourier) return;

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          // Transition: initial → delay, or reset delay window
          const delayDuration = DELAY_MINUTES * 60;
          setTotalDuration(delayDuration);
          setPhase("delay");
          return delayDuration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  }, [isCompleted, timeLeft, hasCourier]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateCircleDasharray = () => {
    if (totalDuration === 0) return FULL_DASH_ARRAY;
    const fraction = timeLeft / totalDuration;
    const adjusted = fraction - (1 / totalDuration) * (1 - fraction);
    return (Math.max(0, adjusted) * FULL_DASH_ARRAY).toFixed(0);
  };

  const formatTimeLeft = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const strokeColor = isCompleted ? "#22C55E" : "#CE2600";

  const message =
    phase === "delay"
      ? "We've experienced some unexpected delay, delivery time has been increased by another 10 mins."
      : "Delivery estimates are influenced by the preparation time of your selected items.";
  
  // Don't show timer if no courier assigned
  if (!hasCourier && !isCompleted) {
    return null;
  }

  return (
    <div>
      <div
        className="relative bg-white dark:bg-[#121212] rounded-full shadow-md border dark:border-[#505050] pl-2 pt-1 max-w-[348px] mx-auto"
        data-test="timerComponent"
      >
        <svg
          width="331"
          height="332"
          viewBox="0 0 331 332"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="transform -scale-x-100"
        >
          <circle
            cx="165.66"
            cy="165.755"
            r="161.309"
            stroke="#9A9898"
            strokeWidth="8"
            strokeDasharray="36 10"
          />
          <circle
            cx="165.66"
            cy="165.755"
            r="161.309"
            strokeWidth="9"
            stroke={strokeColor}
            strokeDasharray="1015"
            strokeDashoffset={calculateCircleDasharray()}
            className="progress"
          />
        </svg>

        {isCompleted ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-bold text-4xl text-green-500 max-w-[200px] text-center">
              Order Completed
            </span>
          </div>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {phase === "delay" && (
              <div className="bg-[#CE260030] tracking-widest w-fit rounded-md px-2 py-0.5 mb-2">
                <span className="font-bold text-red-500 text-sm uppercase">DELAYED</span>
              </div>
            )}
            <span className="font-bold text-6xl text-primary">
              {formatTimeLeft(timeLeft)}
            </span>
            <span className={`max-w-[200px] text-xl font-medium text-center mt-4 ${phase === "delay" ? "text-[#676565]" : ""}`}>
              {phase === "delay" ? "Additional Minutes" : "Minutes until delivered"}
            </span>
          </div>
        )}


      </div>
      {/* Status message below the timer */}
      {!isCompleted && (
        <p
          className={`text-center text-sm px-4 pb-4 pt-2 ${phase === "delay" ? "text-red-500" : "text-gray-500 dark:text-[#BCBCBC]"
            }`}
        >
          {message}
        </p>
      )}
    </div>
  );
}
